import React, { useState } from "react";
import { toast } from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import styles from "./styles.module.css";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import Button from "../../../components/button-v1";
import { handleApiError } from "../../../utils/handleApiError";
import {
  createPlaceOfService,
  updatePlaceOfService,
} from "../../../services/place-of-service";

// Add a place of service to a location, or rename an existing one when
// `placeOfService` is passed. The location's own entry is never renamed
// here - it follows the location's name.
const AddPlaceOfServiceModal = ({
  isOpen,
  onClose,
  location,
  placeOfService,
  existingPlaces = [],
  onSaved,
}) => {
  const isRename = Boolean(placeOfService);
  const [name, setName] = useState(placeOfService?.name || "");
  const queryClient = useQueryClient();

  const mutation = useMutation(
    trimmedName =>
      isRename
        ? updatePlaceOfService(placeOfService.id, { name: trimmedName })
        : createPlaceOfService(location.id, { name: trimmedName }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["places-of-service", location.id]);
        toast.success(
          isRename
            ? "Place of service renamed successfully"
            : "Place of service added successfully"
        );
        onSaved?.();
        onClose();
      },
      onError: handleApiError,
    }
  );

  const handleSubmit = e => {
    e.preventDefault();
    const trimmedName = name.trim();

    if (!trimmedName) {
      toast.error("Name is required.");
      return;
    }

    if (isRename && trimmedName === placeOfService.name) {
      onClose();
      return;
    }

    // Names are unique per location, case-insensitive (backend rule)
    const isDuplicate = existingPlaces.some(
      place =>
        place.id !== placeOfService?.id &&
        place.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      toast.error("This location already has a place of service with this name.");
      return;
    }

    mutation.mutate(trimmedName);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} setOpen={onClose}>
      <DialogContent className={styles.modal}>
        <DialogHeader>
          <DialogTitle className={styles.title}>
            {isRename ? "Rename Place of Service" : "Add Place of Service"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <DialogBody className={styles.body}>
            <p className={styles.prompt}>
              {isRename ? (
                <>
                  Enter a new name for <strong>{placeOfService.name}</strong>
                </>
              ) : (
                <>
                  Enter a name for new place of service for location{" "}
                  <strong>{location?.name}</strong>
                </>
              )}
            </p>
            <input
              type="text"
              className={styles.input}
              placeholder="New place of service"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={255}
              autoFocus
            />
          </DialogBody>
          <div className={styles.footer}>
            <Button
              variant="normal"
              className={styles.cancel_btn}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className={styles.submit_btn}
              loading={mutation.isLoading}
            >
              {isRename ? "Save" : "Add New place of service"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddPlaceOfServiceModal;
